import './Paywalls.scss';

import { useCallback, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import Box from 'fe-design-base/atoms/Box';
import Dialog from 'fe-design-base/organisms/Dialog';
import {
  useIsMediumLargeScreen,
  useIsMediumScreen,
} from 'fe-design-base/utils/useMediaQuery';

import { getProductGrowthBundlesExperimentEnabled } from 'selectors/session';

import { cxHelpers } from 'util/className';
import {
  EVENT_ACTIONS,
  PRODUCT_AREAS,
  TRACK_ACTION_TYPES,
} from 'util/tracking_constants';
import {
  useExtendUx,
  useTrackUx,
  useTrackUxOnMount,
  withUxRoot,
} from 'util/uxEvents';

import BundleView from './views/BundleView';
import TierView from './views/TierView';

const { cx, cxEl } = cxHelpers('Paywalls');

interface PaywallsProps {
  tier: 'basic' | 'essentials' | 'plus' | 'all-in-one';
  feature: string;
  focusedFeature?: string;
  isOpen?: boolean;
  onClose?: () => void;
  serverKey?: string;
  locationProperty?: string;
}

const Paywalls = ({
  tier,
  feature,
  focusedFeature,
  isOpen = true,
  onClose,
  serverKey,
  locationProperty,
}: PaywallsProps) => {
  const extendUx = useExtendUx();
  const trackUx = useTrackUx();

  const isMediumScreen = useIsMediumScreen();
  const isMediumLargeScreen = useIsMediumLargeScreen();

  const [isDialogOpen, setIsDialogOpen] = useState(isOpen);

  const isEnrolledProductGrowthBundlesExperiment = useSelector(
    getProductGrowthBundlesExperimentEnabled
  );

  const closeDialog = useCallback(() => {
    onClose?.();
    setIsDialogOpen(false);
  }, [onClose]);

  const handleDismiss = useCallback(() => {
    // Track dismiss clicked
    trackUx(EVENT_ACTIONS.DISMISS_CLICKED, TRACK_ACTION_TYPES.CLICK);
    closeDialog();
  }, [closeDialog, trackUx]);

  const boxWidth = useMemo(() => {
    if (isMediumLargeScreen) return 640;
    if (isMediumScreen) return 480;
    return '100%';
  }, [isMediumLargeScreen, isMediumScreen]);

  // Set up tracking context
  extendUx({
    eventCategory: feature,
    serverKey,
    locationProperty,
  });

  // Track modal viewed on mount
  useTrackUxOnMount(EVENT_ACTIONS.MODAL_VIEWED, TRACK_ACTION_TYPES.VIEW);

  return (
    <Dialog
      className={cx()}
      open={isDialogOpen}
      onClose={handleDismiss}
      fullScreen={false}
      disableBackdropClick
      noPadding
    >
      {() => (
        <Box
          w={boxWidth}
          h={isMediumLargeScreen ? 420 : 'auto'}
          flex={isMediumLargeScreen}
        >
          <Box hidden={!isMediumLargeScreen} className={cxEl('image')}>
            <img
              src={require('./assets/paywalls-modal-background.png')}
              alt="Paywalls Modal Background"
            />
          </Box>

          {isEnrolledProductGrowthBundlesExperiment ? (
            <BundleView onClick={closeDialog} />
          ) : (
            <TierView focusedFeature={focusedFeature} tier={tier} />
          )}
        </Box>
      )}
    </Dialog>
  );
};

export default withUxRoot({
  productArea: PRODUCT_AREAS.PAYWALL,
})(Paywalls);
